import { useState, useCallback, useEffect, type RefObject } from 'react'

// Constants
const SUBMIT_REPLY_KEYBOARD_SHORTCUT = 'Enter'

type UseReplyInteractionsProps = {
  formRef: RefObject<HTMLFormElement>
  textareaRef: RefObject<HTMLTextAreaElement>
  savedRepliesRef: RefObject<HTMLDivElement>
  isExpanded: boolean
  text: string
  isSubmitting: boolean
  onDiscard: () => void
  onKeepWriting: () => void
  onSubmit: () => Promise<void>
}

type UseReplyInteractionsReturn = {
  showDiscardDialog: boolean
  setShowDiscardDialog: (show: boolean) => void
  discard: () => void
  keepWriting: () => void
}

/**
 * Hook for managing UI interactions in reply forms.
 *
 * This hook handles user interactions including:
 * - Click outside behavior with discard protection
 * - Keyboard shortcuts (Cmd/Ctrl + Enter to submit)
 * - Discard dialog state management
 * - Form validation integration
 *
 * **When to use:**
 * - When you need click outside behavior for reply forms
 * - For components that require keyboard shortcuts
 * - When you need discard protection for unsaved changes
 * - As part of a complete reply form experience
 *
 * **Key Features:**
 * - Smart click outside detection (excludes saved replies popover)
 * - Keyboard shortcut support (Cmd/Ctrl + Enter)
 * - Discard dialog for unsaved changes
 * - Form validation before submission
 * - Proper focus management after dialog interactions
 *
 * **Click Outside Logic:**
 * - Only triggers when form is expanded and not submitting
 * - Excludes clicks on saved replies popover
 * - Excludes clicks on buttons (allows button actions)
 * - Shows discard dialog if text exists, otherwise discards immediately
 *
 * @param props - Configuration for interaction behavior
 * @returns Interaction state and handlers
 *
 * @example
 * ```tsx
 * const interactions = useReplyInteractions({
 *   formRef,
 *   textareaRef,
 *   savedRepliesRef,
 *   isExpanded: form.isExpanded,
 *   text: form.text,
 *   isSubmitting: submission.isSubmitting,
 *   onDiscard: () => { form.clear(); form.collapse() },
 *   onKeepWriting: () => textareaRef.current?.focus(),
 *   onSubmit: async () => {
 *     if (formRef.current?.checkValidity()) {
 *       await submission.submit()
 *     }
 *   }
 * })
 *
 * // Basic usage
 * <DiscardDialog open={interactions.showDiscardDialog} onDiscard={interactions.discard} />
 * ```
 *
 * @example
 * ```tsx
 * // Custom validation
 * onSubmit: async () => {
 *   if (form.text.length < 10) return
 *   await submission.submit()
 * }
 *
 * // Programmatic control
 * interactions.setShowDiscardDialog(true)
 * ```
 */
export const useReplyInteractions = ({
  formRef,
  textareaRef,
  savedRepliesRef,
  isExpanded,
  text,
  isSubmitting,
  onDiscard,
  onKeepWriting,
  onSubmit,
}: UseReplyInteractionsProps): UseReplyInteractionsReturn => {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!isExpanded || isSubmitting) return

      // Check if the clicked element is the saved replies popover
      // to be able to manage clicks inside the popover
      if (!(event.target instanceof Node)) return
      const target = event.target

      const clickedOutsideForm =
        formRef.current &&
        !formRef.current.contains(target) &&
        !(
          savedRepliesRef.current && savedRepliesRef.current.contains(target)
        ) &&
        // Allow buttons to perform their actions instead of closing the form
        !(event.target instanceof HTMLButtonElement)

      const hasNoText = text.length === 0
      if (clickedOutsideForm && isExpanded && !isSubmitting) {
        if (hasNoText) {
          onDiscard()
        } else {
          setShowDiscardDialog(true)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void =>
      document.removeEventListener('mousedown', handleClickOutside)
  }, [isExpanded, text, isSubmitting, formRef, savedRepliesRef, onDiscard])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isExpanded || !textareaRef.current) return

      const isCmdOrCtrl = event.metaKey || event.ctrlKey

      if (isCmdOrCtrl && event.key === SUBMIT_REPLY_KEYBOARD_SHORTCUT) {
        event.preventDefault()
        void onSubmit()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [isExpanded, textareaRef, onSubmit])

  // Discard logic
  const discard = useCallback(() => {
    onDiscard()
    setShowDiscardDialog(false)
  }, [onDiscard])

  const keepWriting = useCallback(() => {
    setShowDiscardDialog(false)
    onKeepWriting()
  }, [onKeepWriting])

  return {
    showDiscardDialog,
    setShowDiscardDialog,
    discard,
    keepWriting,
  }
}
